import { useEffect, type RefObject } from 'react'

export const MODAL_FOCUSABLE_SELECTOR =
  'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

export function getFocusableElements(container: HTMLElement): HTMLElement[] {
  const elements = Array.from(container.querySelectorAll<HTMLElement>(MODAL_FOCUSABLE_SELECTOR))
  return elements.filter((el) => {
    if (el.hasAttribute('disabled')) return false
    if (el.getAttribute('aria-hidden') === 'true') return false
    if (typeof el.checkVisibility === 'function') {
      return el.checkVisibility({ checkOpacity: true, checkVisibilityCSS: true })
    }
    return true
  })
}

function focusFirstElement(container: HTMLElement) {
  const focusables = getFocusableElements(container)
  if (focusables.length > 0) {
    focusables[0].focus()
    return
  }
  try {
    container.focus()
  } catch {
    /* ignore */
  }
}

interface ModalTrapEntry {
  id: symbol
  containerRef: RefObject<HTMLElement | null>
  onClose?: () => void
  closeOnEscape: boolean
  prevFocus: HTMLElement | null
}

const modalStack: ModalTrapEntry[] = []
let globalKeyDownAttached = false

let scrollLockCount = 0
let savedBodyOverflow = ''

function getTopModal(): ModalTrapEntry | undefined {
  return modalStack[modalStack.length - 1]
}

function isTopModalEntry(entry: ModalTrapEntry): boolean {
  const top = getTopModal()
  if (!top || top.id !== entry.id) return false
  const container = entry.containerRef.current
  if (!container) return false
  return top.containerRef.current === container
}

function handleTabTrap(e: KeyboardEvent, container: HTMLElement) {
  const focusables = getFocusableElements(container)
  if (focusables.length === 0) {
    e.preventDefault()
    focusFirstElement(container)
    return
  }

  const first = focusables[0]
  const last = focusables[focusables.length - 1]
  const activeEl = document.activeElement

  if (!container.contains(activeEl)) {
    e.preventDefault()
    first.focus()
    return
  }

  if (focusables.length === 1) {
    e.preventDefault()
    first.focus()
    return
  }

  if (e.shiftKey) {
    if (activeEl === first) {
      e.preventDefault()
      last.focus()
    }
  } else if (activeEl === last) {
    e.preventDefault()
    first.focus()
  }
}

function handleGlobalKeyDown(e: KeyboardEvent) {
  const top = getTopModal()
  if (!top) return

  const container = top.containerRef.current
  if (!container || top.containerRef.current !== container) return

  if (e.key === 'Escape' && top.closeOnEscape && top.onClose) {
    e.preventDefault()
    top.onClose()
    return
  }

  if (e.key === 'Tab') {
    handleTabTrap(e, container)
  }
}

function attachGlobalKeyDown() {
  if (globalKeyDownAttached) return
  document.addEventListener('keydown', handleGlobalKeyDown)
  globalKeyDownAttached = true
}

function detachGlobalKeyDownIfIdle() {
  if (modalStack.length > 0 || !globalKeyDownAttached) return
  document.removeEventListener('keydown', handleGlobalKeyDown)
  globalKeyDownAttached = false
}

function registerModalTrap(entry: Omit<ModalTrapEntry, 'id'>): () => void {
  const stackEntry: ModalTrapEntry = { ...entry, id: Symbol('modal-trap') }
  modalStack.push(stackEntry)
  attachGlobalKeyDown()

  const rafId = requestAnimationFrame(() => {
    if (!isTopModalEntry(stackEntry)) return
    const container = stackEntry.containerRef.current
    if (container) focusFirstElement(container)
  })

  return () => {
    cancelAnimationFrame(rafId)
    const index = modalStack.findIndex((candidate) => candidate.id === stackEntry.id)
    if (index >= 0) modalStack.splice(index, 1)
    detachGlobalKeyDownIfIdle()

    const { prevFocus } = stackEntry
    if (!prevFocus?.isConnected || typeof prevFocus.focus !== 'function') return
    try {
      prevFocus.focus()
    } catch {
      /* Element may not be focusable in some browsers */
    }
  }
}

/** @internal Test-only reset for module-level modal/scroll state. */
export function resetModalFocusTrapStateForTests() {
  modalStack.length = 0
  if (globalKeyDownAttached) {
    document.removeEventListener('keydown', handleGlobalKeyDown)
    globalKeyDownAttached = false
  }
  scrollLockCount = 0
  savedBodyOverflow = ''
  document.body.style.overflow = ''
}

/** @internal Test-only accessors for stack-aware behaviour. */
export function getModalStackDepthForTests(): number {
  return modalStack.length
}

function lockBodyScroll() {
  if (scrollLockCount === 0) {
    savedBodyOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
  }
  scrollLockCount += 1
}

function unlockBodyScroll() {
  if (scrollLockCount <= 0) return
  scrollLockCount -= 1
  if (scrollLockCount === 0) {
    document.body.style.overflow = savedBodyOverflow
  }
}

export interface UseModalFocusTrapOptions {
  active: boolean
  containerRef: RefObject<HTMLElement | null>
  onClose?: () => void
  /** When false, Escape does not call onClose (e.g. mandatory session warning). */
  closeOnEscape?: boolean
}

/**
 * Traps Tab focus inside a modal, moves initial focus in, restores focus on teardown,
 * and optionally closes on Escape. Stack-aware when multiple modals are open.
 */
export function useModalFocusTrap({
  active,
  containerRef,
  onClose,
  closeOnEscape = true,
}: UseModalFocusTrapOptions) {
  useEffect(() => {
    if (!active) return

    const prevFocus =
      document.activeElement instanceof HTMLElement ? document.activeElement : null

    return registerModalTrap({
      containerRef,
      onClose,
      closeOnEscape,
      prevFocus,
    })
  }, [active, closeOnEscape, containerRef, onClose])
}

export function useBodyScrollLock(active: boolean) {
  useEffect(() => {
    if (!active) return
    lockBodyScroll()
    return () => unlockBodyScroll()
  }, [active])
}
